import { pull, uniqBy } from "lodash";

import { OfferProductDto } from "@intelligems/ig-types";
import * as rtk from "@reduxjs/toolkit";
import { type PayloadAction } from "@reduxjs/toolkit";

import { toGqlId } from "../../api/formatters/shopify";
import { Product } from "../../generated";
import { filteredSelectionCount } from "../helpers";
import { IOfferProductsState } from "../types/offerProducts";
import {
  ProductPickerFilterType,
  ProductPickerState,
  SelectedProduct,
} from "../types/productPicker";

const initialProductPickerState: ProductPickerState = {
  allowZeroProducts: false,
  hasNextPage: true,
  endCursor: "",
  filteredSelectionCount: 0,
  search: "",
  filters: {
    product_type: "",
    vendor: "",
    tag: "",
    status: "",
  },
  selectedProducts: [],
  selectedGwpProducts: [],
  newlyAdded: [],
  newlyRemoved: [],
  isGwpRender: false,
  currGwpTierId: null,
  newlyAddedGwp: [],
  newlyRemovedGwp: [],
};
const productPickerSlice = rtk.createSlice({
  name: "productPicker",
  initialState: initialProductPickerState,
  reducers: {
    initProductPicker: (
      draft,
      action: PayloadAction<{ allowZeroProducts?: boolean }>
    ) => {
      draft = {
        ...initialProductPickerState,
        allowZeroProducts: action.payload.allowZeroProducts || false,
      };
      return draft;
    },
    resetProductPicker: (draft) => {
      draft = initialProductPickerState;
      return draft;
    },
    resetFilters: (draft) => {
      draft.filters = {
        product_type: "",
        vendor: "",
        status: "",
        tag: "",
      };
      return draft;
    },
    setSearch: (draft, action: PayloadAction<{ value: string }>) => {
      draft.search = action.payload.value;
      return draft;
    },
    setFilter: (
      draft,
      action: PayloadAction<{ filter: ProductPickerFilterType; value: string }>
    ) => {
      draft.filters[action.payload.filter] = action.payload.value;
      return draft;
    },
    setPageInfo: (
      draft,
      action: PayloadAction<{ hasNextPage: boolean; endCursor: string | null }>
    ) => {
      draft.hasNextPage = action.payload.hasNextPage;
      draft.endCursor = action.payload.endCursor;
      return draft;
    },
    setFilteredProductCount: (
      draft,
      action: PayloadAction<{ value: number }>
    ) => {
      draft.filteredSelectionCount = action.payload.value;
      return draft;
    },
    clearAndAddProductsToSelection: (
      draft,
      action: PayloadAction<{ products: SelectedProduct[] }>
    ) => {
      draft.selectedProducts = [];
      let index;
      for (const product of action.payload.products) {
        index = draft.selectedProducts.findIndex(
          (product) => product.id === product.id
        );
        draft.selectedProducts.push(product);
      }
      draft.selectedProducts = uniqBy(draft.selectedProducts, (i) => i.id);
      return draft;
    },
    toggleProduct: (
      draft,
      action: PayloadAction<{
        products?: SelectedProduct[] | Product[];
        product: SelectedProduct | Product;
      }>
    ) => {
      let index;
      if (draft.isGwpRender && draft.currGwpTierId) {
        index = draft.selectedGwpProducts.findIndex(
          (selectedGwp) => selectedGwp.tierId === draft.currGwpTierId
        );

        if (index >= 0) {
          draft.selectedGwpProducts.splice(index, 1);
          pull(draft.newlyAddedGwp, action.payload.product.id);
          draft.newlyRemovedGwp.push(action.payload.product);
        } else {
          draft.selectedGwpProducts.push({
            tierId: draft.currGwpTierId,
            product: action.payload.product,
          });
          const removeIndex = draft.newlyRemovedGwp.findIndex(
            (prod) =>
              toGqlId(prod.id, "Product") ===
              toGqlId(action.payload.product.id, "Product")
          );
          if (removeIndex >= 0) {
            draft.newlyRemovedGwp.splice(removeIndex, 1);
          }
          draft.newlyAddedGwp.push(action.payload.product.id);
        }
        draft.filteredSelectionCount = -1;
      } else {
        index = draft.selectedProducts.findIndex(
          (product) =>
            toGqlId(product.id, "Product") ===
            toGqlId(action.payload.product.id, "Product")
        );

        if (index >= 0) {
          draft.selectedProducts.splice(index, 1);
          pull(draft.newlyAdded, action.payload.product.id);
          draft.newlyRemoved.push(action.payload.product);
        } else {
          draft.selectedProducts.push(action.payload.product);
          const removeIndex = draft.newlyRemoved.findIndex(
            (prod) =>
              toGqlId(prod.id, "Product") ===
              toGqlId(action.payload.product.id, "Product")
          );
          if (removeIndex >= 0) {
            draft.newlyRemoved.splice(removeIndex, 1);
          }
          draft.newlyAdded.push(action.payload.product.id);
        }

        if (action.payload.products) {
          draft.filteredSelectionCount = filteredSelectionCount(
            action.payload.products,
            draft.selectedProducts
          );
        } else {
          draft.filteredSelectionCount = -1;
        }
      }
      return draft;
    },
    handleSelectMultipleProductsOnPage: (
      draft,
      action: PayloadAction<{
        products: Product[] | SelectedProduct[];
        select: boolean;
      }>
    ) => {
      for (const product of action.payload.products) {
        const index = draft.selectedProducts.findIndex(
          (p) => toGqlId(p.id, "Product") === toGqlId(product.id, "Product")
        );
        if (index >= 0 && !action.payload.select) {
          draft.selectedProducts.splice(index, 1);
        } else if (index === -1 && action.payload.select) {
          draft.selectedProducts.push(product);
        }
      }

      draft.filteredSelectionCount = filteredSelectionCount(
        action.payload.products,
        draft.selectedProducts
      );
      return draft;
    },
    removeByProductIds: (
      draft,
      action: PayloadAction<{
        productIds: string[];
      }>
    ) => {
      draft.selectedProducts = draft.selectedProducts.filter(
        (selProd) =>
          !action.payload.productIds.some(
            (prod) =>
              toGqlId(prod, "Product") === toGqlId(selProd.id, "Product")
          )
      );
      return draft;
    },
    removeByProductId: (
      draft,
      action: PayloadAction<{
        productId: string;
      }>
    ) => {
      const index = draft.selectedProducts.findIndex(
        (product) =>
          toGqlId(product.id, "Product") ===
          toGqlId(action.payload.productId, "Product")
      );

      if (index !== -1) {
        draft.selectedProducts.splice(index, 1);
      }
      return draft;
    },
    clearNewlyAddedSelectedProducts: (draft) => {
      if (
        draft.isGwpRender &&
        draft.currGwpTierId &&
        draft.newlyAddedGwp.length > 0
      ) {
        draft.selectedGwpProducts = draft.selectedGwpProducts.filter(
          (gwp) =>
            !draft.newlyAddedGwp.some(
              (newProd) =>
                toGqlId(newProd, "Product") ===
                  toGqlId(gwp.product.id, "Product") &&
                gwp.tierId === draft.currGwpTierId
            )
        );
        draft.newlyAddedGwp = [];
      } else {
        draft.selectedProducts = draft.selectedProducts.filter(
          (product) =>
            !draft.newlyAdded.some(
              (newProd) =>
                toGqlId(newProd, "Product") === toGqlId(product.id, "Product")
            )
        );
        draft.newlyAdded = [];
      }
      return draft;
    },
    addProductsToSelection: (
      draft,
      action: PayloadAction<{
        products: SelectedProduct[];
      }>
    ) => {
      let index;
      for (const product of action.payload.products) {
        index = draft.selectedProducts.findIndex(
          (product) => product.id === product.id
        );
        draft.selectedProducts.push(product);
      }
      draft.selectedProducts = uniqBy(draft.selectedProducts, (i) => i.id);
      return draft;
    },
    addProductToGwpSelection: (
      draft,
      action: PayloadAction<{
        tierId: string;
        product: SelectedProduct | Product;
      }>
    ) => {
      draft.selectedGwpProducts.push({
        tierId: action.payload.tierId,
        product: action.payload.product,
      });
      return draft;
    },
    addNewlyRemovedSelectedProducts: (draft) => {
      if (
        draft.newlyRemovedGwp.length > 0 &&
        draft.isGwpRender &&
        draft.currGwpTierId
      ) {
        draft.selectedGwpProducts = draft.selectedGwpProducts.concat({
          tierId: draft.currGwpTierId,
          product: draft.newlyRemovedGwp[0],
        });

        draft.newlyRemovedGwp = [];
      } else {
        draft.selectedProducts = draft.selectedProducts.concat(
          draft.newlyRemoved
        );
        draft.newlyRemoved = [];
      }
      return draft;
    },
    clearNewlyAdded: (draft) => {
      draft.newlyAdded = [];
      draft.newlyAddedGwp = [];
      return draft;
    },
    clearNewlyRemoved: (draft) => {
      draft.newlyRemoved = [];
      draft.newlyRemovedGwp = [];
      return draft;
    },
    clearAll: (draft) => {
      draft.selectedProducts = [];
      draft.selectedGwpProducts = [];
      return draft;
    },
    removeGwpTier: (draft) => {
      let index;
      if (draft.currGwpTierId) {
        index = draft.selectedGwpProducts.findIndex(
          (selectedGwp) => selectedGwp.tierId === draft.currGwpTierId
        );

        if (index >= 0) {
          draft.selectedGwpProducts.splice(index, 1);
        }
      }
      return draft;
    },
    setAllowZeroProducts: (
      draft,
      action: PayloadAction<{
        value: boolean;
      }>
    ) => {
      draft.allowZeroProducts = action.payload.value;
      return draft;
    },
    setRequiresSellingPlan: (
      draft,
      action: PayloadAction<{
        productId: string;
        requiresSellingPlan: boolean;
      }>
    ) => {
      const index = draft.selectedProducts.findIndex(
        (product) =>
          toGqlId(product.id, "Product") ===
          toGqlId(action.payload.productId, "Product")
      );

      if (index !== -1) {
        draft.selectedProducts[index].requiresSellingPlan =
          action.payload.requiresSellingPlan;
      }
      return draft;
    },
    toggleIsGwpRender: (
      draft,
      action: PayloadAction<{
        value: boolean;
      }>
    ) => {
      draft.isGwpRender = action.payload.value;
      return draft;
    },
    setCurrGwpTierId: (
      draft,
      action: PayloadAction<{
        value: string;
      }>
    ) => {
      draft.currGwpTierId = action.payload.value;
      return draft;
    },
  },
  selectors: {
    selectedProductPickerState: (state) => state as ProductPickerState,
  },
});

export const {
  resetFilters,
  setFilteredProductCount,
  resetProductPicker,
  setFilter,
  clearAndAddProductsToSelection,
  setPageInfo,
  setSearch,
  setRequiresSellingPlan,
  addNewlyRemovedSelectedProducts,
  addProductToGwpSelection,
  clearNewlyAddedSelectedProducts,
  clearNewlyRemoved,
  addProductsToSelection,
  clearNewlyAdded,
  removeByProductId,
  removeByProductIds,
  handleSelectMultipleProductsOnPage,
  clearAll,
  setCurrGwpTierId,
  removeGwpTier,
  toggleIsGwpRender,
  toggleProduct,
  setAllowZeroProducts,
  initProductPicker,
} = productPickerSlice.actions;

export const productPickerReducer = productPickerSlice.reducer;
export const { selectedProductPickerState } = productPickerSlice.selectors;
